import type {FC, KeyboardEvent, ReactElement, SyntheticEvent} from "react";
import type React from "react";
import {cloneElement, memo, useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";

function ownerDocument(node: Node | null | undefined): Document {
    return node?.ownerDocument || document;
}

function ownerWindow(node: Node | undefined): Window {
    const doc = ownerDocument(node);
    return doc.defaultView || window;
}

export interface Props {
    open: boolean;
    children: ReactElement<any>;
    backdrop?: ReactElement<any>;
    container?: HTMLElement;
    hideBackdrop?: boolean;
    disableEscapeKeyDown?: boolean;
    onOpen?: () => void;
    onClose?: (e: SyntheticEvent, reason: string) => void;
    onKeyDown?: (e: KeyboardEvent) => void;
    onBackdropClick?: (e: React.SyntheticEvent) => void;
}

const Overlay: FC<Props> = ({
    open = false,
    children,
    backdrop = <div className="image-preview-backdrop" />,
    container = document.body,
    disableEscapeKeyDown,
    hideBackdrop,
    onBackdropClick,
    onOpen,
    onClose,
    onKeyDown
}) => {
    const [, setOriginalProperties] = useState<[HTMLElement, string, string?][]>([]);
    const [mountNode, setMountNode] = useState<HTMLElement>(container);
    const modalRef = useRef<HTMLDivElement>(null);

    const handleMounted = useCallback(() => {
        onOpen?.();

        const touchAction = container?.style.touchAction;
        setOriginalProperties(prevState => [
            ...prevState,
            [container, "touch-action", touchAction]
        ]);
        container.style.touchAction = "none";

        const parent = container.parentElement;
        const containerWindow = ownerWindow(container);
        const scrollContainer =
            parent?.nodeName === "HTML" &&
            containerWindow.getComputedStyle(parent).overflowY === "scroll"
                ? parent
                : container;

        const {overflow} = scrollContainer.style;
        setOriginalProperties(prevState => [...prevState, [scrollContainer, "overflow", overflow]]);
        scrollContainer.style.overflow = "hidden";

        if (modalRef.current) {
            modalRef.current.scrollTop = 0;
        }
    }, [onOpen, container]);

    const handleClose = useCallback(() => {
        if (!open) {
            setOriginalProperties(prev => {
                prev.forEach(([el, prop, value]) => {
                    if (value) {
                        el.style.setProperty(prop, value);
                    } else {
                        el.style.removeProperty(prop);
                    }
                });
                return [];
            });
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            handleMounted();
        } else {
            handleClose();
        }
    }, [open, handleMounted, handleClose]);

    useEffect(() => {
        setMountNode(container || document.body);
    }, [container]);

    const handleBackdropClick = useCallback(
        (event: SyntheticEvent) => {
            if (event.target !== event.currentTarget) {
                return;
            }

            onBackdropClick?.(event);
            onClose?.(event, "backdropClick");
        },
        [onBackdropClick, onClose]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            onKeyDown?.(event);

            if (event.key !== "Escape") {
                return;
            }

            if (!disableEscapeKeyDown) {
                // Swallow the event, in case someone is listening for the escape key on the body.
                event.stopPropagation();
                onClose?.(event, "escapeKeyDown");
            }
        },
        [disableEscapeKeyDown, onClose, onKeyDown]
    );

    if (!open) {
        return null;
    }

    const content = (
        <div
            ref={modalRef}
            className="image-slides-portal"
            role="presentation"
            onKeyDown={handleKeyDown}
        >
            {!hideBackdrop && backdrop
                ? cloneElement(backdrop, {
                      onClick: handleBackdropClick
                  })
                : null}
            {cloneElement(children, {})}
        </div>
    );

    return mountNode ? createPortal(content, mountNode) : null;
};

Overlay.displayName = "Overlay";

export default memo(Overlay);
